// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { ServerAvatar } from '@/components/server/server-avatar';
import type { OnlineUser } from '../presence/presence-list';
import { CalendarIcon, Mic } from 'lucide-react';
import { useSocket } from '@/context/socket-context/use-external-socket';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ServerEvent } from '@/models';
import { ChannelType } from '@/models';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE, GAME_POLLING_INTERVAL } from '@/config';
import { useSettings } from '@/context/settings-context/settings-context';
import { useVoiceChannelNotification } from '@/hooks/use-channel-voice-notifications';
import { useAuth } from '@/context/auth-context/use-auth';
import { ServerPageLoaderData } from '@/pages/server/server-page.tsx';
import { Tooltip } from '@heroui/react';
import { motion } from 'framer-motion';
import { useServerGamesStore } from '@/stores/use-server-games-store.ts';
import { useBreakpoint } from '@/hooks/use-breakpoint.ts';
import { useServerNotificationMute } from '@/hooks/use-server-notifications-mute.ts';
import { BsBellFill, BsBellSlashFill } from 'react-icons/bs';

interface NavigationItemProps {
  id: string;
  imageUrl?: string;
  name: string;
  onClick?: (id: string) => void;
}

export const NavigationItem = ({ id, imageUrl, name, onClick }: NavigationItemProps) => {
  const { serverId } = useParams<{ serverId: string }>();
  const navigate = useNavigate();
  const socket = useSocket();
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [events, setEvents] = useState<ServerEvent[]>([]);
  const [opacity, setOpacity] = useState(1);
  const { notifications, toggleServerMute } = useSettings();
  const { user } = useAuth();
  const { fetchServerGames, getServerGames } = useServerGamesStore();
  const games = getServerGames(id) ?? [];
  const [mostPlayedGameData, setMostPlayedGameData] = useState<{
    gameName: string;
    icon: string | null;
  } | null>(null);
  const [context, setContext] = useState<{
    x: string | number | undefined;
    y: string | number | undefined;
  } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const { isServerMuted } = useServerNotificationMute(id);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleClick = (e: any) => {
    if (isDragging) return;

    if (onClick) {
      onClick(id);
    } else {
      navigate(`/servers/${id}`);
    }
  };

  const mergedListeners = {
    ...listeners,
    onClick: handleClick,
    onTouchStart: handleClick,
  };

  const onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    if (context) {
      closeContextMenu();
    } else {
      recalcContextPosition();
    }
  };

  const recalcContextPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();

    setContext({
      x: rect.right + 6,
      y: rect.top + 4,
    });
  }, []);

  const closeContextMenu = () => setContext(null);

  useEffect(() => {
    const refetch = async () => {
      fetchServerGames(id);
    };

    refetch();

    const intervalId = setInterval(refetch, GAME_POLLING_INTERVAL * 2);
    return () => clearInterval(intervalId);
  }, [id]);

  useEffect(() => {
    if (!games || Object.keys(games).length === 0) {
      setMostPlayedGameData(null);
      return;
    }

    const gameCount: Record<string, { count: number; icon: string | null }> = {};

    for (const userId in games) {
      const game = games[userId];
      if (!game) continue;

      const currentGame = games.find((g) => !g.endedAt);
      if (!currentGame) continue;

      const proc = currentGame.process;
      if (!gameCount[proc]) {
        gameCount[proc] = {
          count: 1,
          icon: currentGame.application?.iconUrl ?? null,
        };
      } else {
        gameCount[proc].count += 1;
      }
    }

    const sorted = Object.entries(gameCount).sort((a, b) => b[1].count - a[1].count);
    const [process, data] = sorted[0] || [];

    // TODO: think about showing when?
    if (process && voiceChannelUsers.length > 0) {
      setMostPlayedGameData({
        gameName: process,
        icon: data.icon,
      });
    } else {
      setMostPlayedGameData(null);
    }
  }, [games]);

  // TODO: fix notification that "Пользователь присоединился к каналу general" - текстовому
  const onlineUsersInVoiceChannels = useCallback(() => {
    return onlineUsers.filter((user) =>
      user.devices.some(
        (device) => device.serverId === id && device.channelType !== ChannelType.TEXT
      )
    );
  }, [onlineUsers, id]);

  const voiceChannelUsers = useMemo(
    () => onlineUsersInVoiceChannels(),
    [onlineUsersInVoiceChannels]
  );

  useVoiceChannelNotification({
    notifications: notifications,
    voiceChannelUsers: voiceChannelUsers,
    id: id,
    name: name,
    userId: user?.id,
  });

  useEffect(() => {
    if (onlineUsers.length == 0) {
      setOpacity(0);
    } else {
      setOpacity(1);
    }
  }, [onlineUsers.length]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/servers/${id}/events`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        const data = await res.json();

        const now = new Date();
        data.forEach((event: ServerEvent) => {
          const startedEvents = data.filter((event: ServerEvent) => {
            const startDate = new Date(event.startDate);
            return now >= startDate && now <= new Date(startDate.getTime() + 15 * 60_000); // Check within 15 minute of start
          });

          if (startedEvents.length > 0) {
            setEvents(startedEvents);
          } else {
            setEvents([]);
          }
        });
      } catch (error) {}
    };

    fetchEvents();

    const interval = setInterval(() => {
      fetchEvents();
    }, 30_000);

    return () => clearInterval(interval);
  }, [id]);

  useEffect(() => {
    if (!socket) return;

    const handleMessage = async (event: MessageEvent) => {
      const data = JSON.parse(event.data);

      if (data.type === 'presence-update') {
        // TODO: add ONLY when user is in channel of current server?
        const updatedUsers: OnlineUser[] = data.users
          .filter(
            (user: OnlineUser) =>
              user.devices.some((device) => device.serverId === id) && user.status !== 'invisible'
          )
          .map((user: OnlineUser) => ({
            ...user,
            devices: user.devices || [],
          }));
        setOnlineUsers(updatedUsers);
      }
    };

    socket.addEventListener('message', handleMessage);

    return () => {
      socket.removeEventListener('message', handleMessage);
    };
  }, [socket, id]);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (!context || !menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) {
        closeContextMenu();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [context]);

  useEffect(() => {
    if (!context) return;

    recalcContextPosition();
    window.addEventListener('resize', recalcContextPosition);

    return () => window.removeEventListener('resize', recalcContextPosition);
  }, [context, recalcContextPosition]);

  return (
    <Tooltip
      placement="right"
      offset={2}
      content={
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          transition={{ duration: 0.15 }}
          className="px-1.5 py-1 rounded-md dark:text-white text-sm font-medium max-w-[200px]"
        >
          {name}
        </motion.div>
      }
      classNames={{
        base: 'border-none shadow-none bg-transparent p-0',
      }}
    >
      <button
        ref={(el) => {
          setNodeRef(el);
          buttonRef.current = el;
        }}
        style={style}
        onContextMenu={onContextMenu}
        onPointerUp={handleClick}
        {...attributes}
        {...listeners}
        className="group relative flex items-center cursor-pointer"
      >
        {context && (
          <div
            ref={menuRef}
            className="fixed bg-popover border rounded-md shadow-md z-[1001] px-1 py-1"
            style={{ top: context.y, left: context.x }}
          >
            <button
              className="px-3 py-2 hover:bg-accent w-full min-h-[48px] text-left text-sm flex gap-2 items-center"
              onClick={() => {
                toggleServerMute(id);
                setContext(null);
              }}
            >
              {isServerMuted ? (
                <>
                  <BsBellSlashFill className="text-red-500" />
                  {name} Заглушено
                </>
              ) : (
                <>
                  <BsBellFill className="text-green-500" />
                  {name} Уведомлять
                </>
              )}
            </button>
          </div>
        )}
        <style>{`
          .fade-in {
            opacity: 1;
            transition: opacity 0.2s ease-in-out;
          }

          .fade-out {
            opacity: 0;
            transition: opacity 0.2s ease-in-out;
          }
        `}</style>
        <div
          className={cn(
            'absolute left-0 bg-primary rounded-r-full w-[4px]',
            serverId !== id && 'group-hover:h-[20px]',
            serverId === id ? 'h-[36px]' : 'h-[8px]'
          )}
        />
        <div
          className={cn(
            'relative group flex mx-3 h-[48px] w-[48px] rounded-[24px] group-hover:rounded-[16px] transition-all overflow-hidden',
            serverId === id && 'bg-primary/10 text-primary rounded-[16px]'
          )}
        >
          <ServerAvatar
            imageUrl={imageUrl}
            name={name}
            seed={id}
            alt="Channel"
            className="transition-all group-hover:bg-gray-500"
          />
        </div>
        {onlineUsersInVoiceChannels().length > 0 && (
          <div className="absolute right-2 top-0 flex size-[18px] items-center justify-center rounded-full border border-white bg-red-500 shadow-lg shadow-red-500">
            <Mic className="size-3 text-white drop-shadow-[0_0_6px_rgba(255,0,0,0.8)]" />
          </div>
        )}
        <div
          className={cn(
            'absolute bottom-0 right-2 w-[18px] h-[18px] bg-green-500 border-white border rounded-full flex items-center justify-center'
          )}
          style={{ opacity }}
        >
          <span className="text-[10px] font-semibold text-white">{onlineUsers.length}</span>
        </div>
        {events.length > 0 && (
          <div className="absolute bottom-0 left-1 flex h-[18px] w-[22px] items-center justify-center rounded-full bg-black">
            <CalendarIcon className="size-3 text-white" />
          </div>
        )}
        {mostPlayedGameData?.icon && (
          <div className="absolute left-1 top-0 flex size-[18px] items-center justify-center rounded-full bg-black">
            <img
              src={mostPlayedGameData.icon}
              alt={mostPlayedGameData.gameName}
              className="size-4 rounded-sm"
              title={mostPlayedGameData.gameName}
            />
          </div>
        )}
      </button>
    </Tooltip>
  );
};
